import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { apiStatus } from 'shared/constants/AppEnum'
import { razorpayBankAccountApi } from 'api/admin'
import { AppState } from 'store/reduxStore'
import { RazorpayBankAccountType } from 'types/api/temple'
import { IApiState } from 'store/types'

const adapter = createEntityAdapter<RazorpayBankAccountType>()

type StateType = {
    listApi: IApiState,
    createApi: IApiState,
    updateApi: IApiState,
    deleteApi: IApiState
}

const initialApi: StateType = {
    listApi: { status: apiStatus.IDLE, error: null },
    createApi: { status: apiStatus.IDLE, error: null },
    updateApi: { status: apiStatus.IDLE, error: null },
    deleteApi: { status: apiStatus.IDLE, error: null },
}

export const staffRazorpayListThunk = createAsyncThunk(
    'staff/razorpay/list',
    async (payload: UrlType | any, { rejectWithValue }) => {
        try {
            const response = await razorpayBankAccountApi.list(payload)
            // normalize keys
            response.data.forEach((i: any) => { i.merchantId = i.merchant_id ?? i.merchantId })
            return { data: response.data }
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffRazorpayCreateThunk = createAsyncThunk(
    'staff/razorpay/create',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await razorpayBankAccountApi.create(payload)
            const item = response.data
            item.merchantId = (item as any).merchant_id ?? item.merchantId
            return item
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffRazorpayRetrieveThunk = createAsyncThunk(
    'staff/razorpay/retrieve',
    async (payload: UrlType, { rejectWithValue }) => {
        try {
            const response = await razorpayBankAccountApi.get(payload.url)
            const item = response.data
            item.merchantId = (item as any).merchant_id ?? item.merchantId
            return item
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffRazorpayUpdateThunk = createAsyncThunk(
    'staff/razorpay/update',
    async (payload: PayloadWithUrl<Partial<RazorpayBankAccountType>>, { rejectWithValue }) => {
        try {
            const response = await razorpayBankAccountApi.update(payload.url, payload.data)
            const item = response.data
            item.merchantId = (item as any).merchant_id ?? item.merchantId
            return item
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffRazorpayDeleteThunk = createAsyncThunk(
    'staff/razorpay/delete',
    async (payload: UrlType, { rejectWithValue }) => {
        try {
            await razorpayBankAccountApi.delete(payload.url)
            return payload.url
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

const slice = createSlice({
    name: 'razorpayBankAccount',
    initialState: adapter.getInitialState(initialApi),
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(staffRazorpayListThunk.pending, (state) => {
            state.listApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(staffRazorpayListThunk.fulfilled, (state, action) => {
            state.listApi = { status: apiStatus.SUCCEEDED, error: null }
            adapter.upsertMany(state, action.payload.data)
        })
        builder.addCase(staffRazorpayListThunk.rejected, (state, action) => {
            state.listApi = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(staffRazorpayCreateThunk.pending, (state) => {
            state.createApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(staffRazorpayCreateThunk.fulfilled, (state, action) => {
            state.createApi = { status: apiStatus.SUCCEEDED, error: null }
            adapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffRazorpayCreateThunk.rejected, (state, action) => {
            state.createApi = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(staffRazorpayRetrieveThunk.fulfilled, (state, action) => {
            adapter.upsertOne(state, action.payload)
        })

        builder.addCase(staffRazorpayUpdateThunk.fulfilled, (state, action) => {
            state.updateApi = { status: apiStatus.SUCCEEDED, error: null }
            adapter.upsertOne(state, action.payload)
        })

        builder.addCase(staffRazorpayDeleteThunk.fulfilled, (state, action) => {
            adapter.removeOne(state, action.payload)
            state.deleteApi = { status: apiStatus.SUCCEEDED, error: null }
        })
    }
})

export default slice.reducer

export const {
    selectAll: selectAllRazorpayBankAccounts,
    selectById: selectRazorpayBankById,
} = adapter.getSelectors((state: AppState) => state.razorpayBankAccount)

export const selectRazorpayListApi = (state: AppState) => state.razorpayBankAccount.listApi
export const selectRazorpayCreateApi = (state: AppState) => state.razorpayBankAccount.createApi
export const selectRazorpayUpdateApi = (state: AppState) => state.razorpayBankAccount.updateApi
export const selectRazorpayDeleteApi = (state: AppState) => state.razorpayBankAccount.deleteApi
